package com.siriusxm.unifiedcontent.console.player

import androidx.compose.runtime.*
import contentingestion.unifiedmodel.EntityType
import contentingestion.unifiedmodel.ReferenceIdentifiable
import kotlinx.coroutines.CancellationException
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Resolves the displayed cut's related entities only while the details panel is expanded. Successful
 * names are cached for this track by the parent; changing references disposes their lookup effects,
 * so a slow response cannot replace the next cut's names. Failures remain visible and can be retried
 * by reopening the panel, without interrupting audio or hiding a catalog data problem.
 */
@Composable
internal fun RelatedPlaybackEntities(
    references: List<ReferenceIdentifiable>,
    resolveName: suspend (ReferenceIdentifiable) -> String,
    names: MutableMap<ReferenceIdentifiable, String>,
    entityHref: (ReferenceIdentifiable) -> String?,
) {
    Div({ classes(AudioPlayerStyles.relatedEntities) }) {
        references.forEachIndexed { index, reference ->
            if (index > 0) {
                Span({ classes(AudioPlayerStyles.relatedSeparator); attr("aria-hidden", "true") }) { Text("•") }
            }
            key(reference) {
                var name by remember { mutableStateOf(names[reference]) }
                var failed by remember { mutableStateOf(false) }
                LaunchedEffect(reference) {
                    if (name == null) {
                        try {
                            val resolved = resolveName(reference)
                            names[reference] = resolved
                            name = resolved
                        } catch (cancellation: CancellationException) {
                            throw cancellation
                        } catch (_: Exception) {
                            failed = true
                        }
                    }
                }
                val label = when (reference.type) {
                    EntityType.SHOW -> "Show"
                    EntityType.EPISODE_LINEAR -> "Episode"
                    else -> reference.type.name.replace('-', ' ').replaceFirstChar { it.uppercase() }
                }
                val href = entityHref(reference)
                val text = name ?: if (failed) "Unable to load $label details" else "Loading $label…"
                if (href != null && name != null) {
                    A(href, attrs = {
                        classes(AudioPlayerStyles.relatedLink)
                        title("Open $label: $text")
                    }) { Text(text) }
                } else {
                    Span({ title(reference.id) }) { Text(text) }
                }
            }
        }
    }
}
